import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ArrowLeft, Calculator, Clock, MapPin, Users, Pencil, Trash2, X, Check, Printer } from 'lucide-react';
import RHBradfordPanel from './RHBradfordPanel';
import RHResultCard from './RHResultCard';
import { toast, confirm as confirmModal } from '../ui/Toast';

const RHStudyDetail = ({ studyId, onNavigate }) => {
  const [study, setStudy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('chain');
  const [error, setError] = useState('');

  // Modal calcul
  const [showCalcModal, setShowCalcModal] = useState(false);
  const [calcForm, setCalcForm] = useState({
    rte: '', rte_source: '',
    pa: '', pa_source: '',
    d: '', d_source: '',
    temps_creatif_heures: '',
    control_reference: ''
  });
  const [calculating, setCalculating] = useState(false);
  const [calcError, setCalcError] = useState('');

  // Édition métadonnées
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);

  // Suppression
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const token = localStorage.getItem('oracle_token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchStudy = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/api/rh/studies/${studyId}`, { headers });
      setStudy(res.data);
    } catch (err) {
      setError('Impossible de charger l\'étude');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStudy();
  }, [studyId]);

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const map = {
      draft: { label: 'Brouillon', cls: 'bg-gray-100 text-gray-700' },
      measuring: { label: 'Mesure', cls: 'bg-blue-100 text-blue-700' },
      calculating: { label: 'Calcul', cls: 'bg-yellow-100 text-yellow-700' },
      attributing: { label: 'Attribution', cls: 'bg-purple-100 text-purple-700' },
      converting: { label: 'Conversion', cls: 'bg-indigo-100 text-indigo-700' },
      completed: { label: 'Terminée', cls: 'bg-green-100 text-green-700' },
      archived: { label: 'Archivée', cls: 'bg-gray-200 text-gray-500' }
    };
    const s = map[status] || { label: status, cls: 'bg-gray-100 text-gray-600' };
    return <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${s.cls}`}>{s.label}</span>;
  };

  const getLevelColor = (level) => {
    const colors = { R1: 'bg-blue-100 text-blue-700', R2: 'bg-green-100 text-green-700', R3: 'bg-yellow-100 text-yellow-700', R4: 'bg-orange-100 text-orange-700', R5: 'bg-red-100 text-red-700' };
    return colors[level] || 'bg-gray-100 text-gray-700';
  };

  const getDomainLabel = (d) => ({ humain: 'Humain', ecologique: 'Écologique', culturel: 'Culturel' }[d] || d);

  // Conditions pour afficher le bouton Calculer
  const canCalculate = study &&
    study.status !== 'completed' &&
    study.chain?.length > 0 &&
    study.bradford?.length > 0;

  // Lancer le calcul RH
  const handleCalculate = async () => {
    const { rte, pa, d, rte_source, pa_source, d_source, temps_creatif_heures, control_reference } = calcForm;

    if (!rte || !pa || !d || !rte_source || !pa_source || !d_source || !temps_creatif_heures) {
      setCalcError('Tous les champs RTE, PA, D (avec sources) et temps créatif sont obligatoires');
      return;
    }

    const rteVal = parseFloat(rte);
    const paVal = parseFloat(pa);
    const dVal = parseFloat(d);

    if ([rteVal, paVal, dVal].some(v => isNaN(v) || v < 0 || v > 1)) {
      setCalcError('RTE, PA et D doivent être des nombres entre 0 et 1');
      return;
    }

    setCalculating(true);
    setCalcError('');
    try {
      await axios.post(`/api/rh/studies/${studyId}/calculate`, {
        rte: rteVal,
        rte_source,
        pa: paVal,
        pa_source,
        d: dVal,
        d_source,
        temps_creatif_heures: parseFloat(temps_creatif_heures),
        control_reference: control_reference || null
      }, { headers });

      setShowCalcModal(false);
      setActiveTab('result');
      await fetchStudy();
    } catch (err) {
      setCalcError(err.response?.data?.message || 'Erreur lors du calcul');
    } finally {
      setCalculating(false);
    }
  };

  // --- Édition métadonnées ---
  const startEditing = () => {
    setEditForm({
      title: study.title,
      level: study.level,
      domain: study.domain,
      sub_domain: study.sub_domain || '',
      location: study.location || '',
      period_start: study.period_start ? study.period_start.split('T')[0] : '',
      period_end: study.period_end ? study.period_end.split('T')[0] : '',
      population_size: study.population_size || ''
    });
    setEditing(true);
  };

  const saveEdit = async () => {
    setEditSaving(true);
    try {
      await axios.put(`/api/rh/studies/${studyId}`, {
        ...editForm,
        population_size: editForm.population_size ? parseInt(editForm.population_size) : null
      }, { headers });
      setEditing(false);
      await fetchStudy();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur lors de la modification');
    } finally {
      setEditSaving(false);
    }
  };

  // --- Suppression ---
  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`/api/rh/studies/${studyId}`, { headers });
      onNavigate('rh');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur lors de la suppression');
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  // --- Rendu ---
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full"></div>
        <span className="ml-3 text-gray-400">Chargement de l'étude...</span>
      </div>
    );
  }

  if (error || !study) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-600 mb-4">{error || 'Étude introuvable'}</p>
        <button onClick={() => onNavigate('rh')} className="text-blue-600 hover:underline">Retour à la liste</button>
      </div>
    );
  }

  const tabs = [
    { id: 'chain', label: 'Chaîne Causale', count: study.chain?.length || 0 },
    { id: 'bradford', label: 'Bradford-Hill', count: study.bradford?.filter(s => s.satisfied)?.length || 0 },
    { id: 'result', label: 'Résultat' },
    { id: 'versions', label: 'Versions', count: study.versions?.length || 0 }
  ];

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-start gap-4 mb-6">
        <button
          onClick={() => onNavigate('rh')}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors mt-1"
        >
          <ArrowLeft size={20} className="text-gray-600" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-2xl font-bold text-gray-900">{study.title}</h1>
            {getStatusBadge(study.status)}
          </div>
          <div className="flex items-center gap-4 text-sm text-gray-500">
            <span className={`px-2 py-0.5 rounded text-xs font-bold ${getLevelColor(study.level)}`}>{study.level}</span>
            <span>{getDomainLabel(study.domain)}</span>
            {study.sub_domain && <span>• {study.sub_domain}</span>}
            {study.location && <span className="flex items-center gap-1"><MapPin size={12} />{study.location}</span>}
            {study.period_start && <span className="flex items-center gap-1"><Clock size={12} />{formatDate(study.period_start)} → {formatDate(study.period_end)}</span>}
            {study.population_size && <span className="flex items-center gap-1"><Users size={12} />{study.population_size.toLocaleString()}</span>}
          </div>
        </div>

        <div className="flex items-center gap-2 no-print">
          {/* Bouton PDF */}
          <button
            onClick={() => {
              const originalTitle = document.title;
              document.title = `Étude RH — ${study.title}`;
              window.print();
              document.title = originalTitle;
            }}
            className="flex items-center gap-2 border border-gray-300 text-gray-700 hover:bg-gray-50 px-3 py-2 rounded-lg transition-colors text-sm"
          >
            <Printer size={15} />
            PDF
          </button>

          {/* Bouton Éditer */}
          {study.status !== 'completed' && (
            <button
              onClick={startEditing}
              className="flex items-center gap-2 border border-gray-300 text-gray-700 hover:bg-gray-50 px-3 py-2 rounded-lg transition-colors text-sm"
            >
              <Pencil size={15} />
              Éditer
            </button>
          )}

          {/* Bouton Supprimer */}
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="flex items-center gap-2 border border-blue-200 text-blue-600 hover:bg-blue-50 px-3 py-2 rounded-lg transition-colors text-sm"
          >
            <Trash2 size={15} />
          </button>

          {/* Bouton Calculer */}
          {canCalculate && (
            <button
              onClick={() => setShowCalcModal(true)}
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2.5 rounded-lg transition-colors shadow-sm"
            >
              <Calculator size={18} />
              Calculer RH
            </button>
          )}
        </div>
      </div>

      {/* Onglets */}
      <div className="flex border-b border-gray-200 mb-6 no-print">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
            {tab.count != null && (
              <span className={`ml-2 px-1.5 py-0.5 rounded-full text-xs ${
                activeTab === tab.id ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-500'
              }`}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ===== TAB : Chaîne Causale ===== */}
      <div className={activeTab === 'chain' ? '' : 'hidden print:block'}>
        <h2 className="hidden print:block text-xl font-bold text-gray-900 mb-4 mt-6 pt-4 border-t border-gray-200">Chaîne Causale</h2>
        {(!study.chain || study.chain.length === 0) ? (
          <div className="text-center py-12 text-gray-500">
            <p>Aucun maillon causal défini.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {study.chain.map((link, index) => (
              <div key={link.id || index} className="bg-white border border-gray-200 rounded-lg p-4 print:break-inside-avoid">
                <div className="flex items-center gap-3 mb-2">
                  <span className="w-7 h-7 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold">
                    {link.chain_order || index + 1}
                  </span>
                  <h3 className="font-medium text-gray-900">{link.description}</h3>
                </div>
                <div className="ml-10 flex items-center gap-4 text-sm text-gray-500">
                  <span className={`px-2 py-0.5 rounded text-xs ${
                    link.source_type === 'official' ? 'bg-blue-50 text-blue-600' :
                    link.source_type === 'academic' ? 'bg-purple-50 text-purple-600' :
                    link.source_type === 'empirical' ? 'bg-yellow-50 text-yellow-600' :
                    'bg-gray-50 text-gray-600'
                  }`}>
                    {link.source_type}
                  </span>
                  <span>{link.source}</span>
                  {link.data_value != null && (
                    <span className="font-mono">{link.data_value} {link.data_unit}</span>
                  )}
                </div>
                {index < study.chain.length - 1 && (
                  <div className="text-center text-gray-300 mt-2 text-lg">↓</div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ===== TAB : Bradford-Hill ===== */}
      <div className={activeTab === 'bradford' ? '' : 'hidden print:block'}>
        <h2 className="hidden print:block text-xl font-bold text-gray-900 mb-4 mt-6 pt-4 border-t border-gray-200">Critères Bradford-Hill</h2>
        <RHBradfordPanel
          studyId={studyId}
          scores={study.bradford || []}
          onSave={fetchStudy}
          readOnly={study.status === 'completed'}
        />
      </div>

      {/* ===== TAB : Résultat ===== */}
      <div className={activeTab === 'result' ? '' : 'hidden print:block'}>
        <h2 className="hidden print:block text-xl font-bold text-gray-900 mb-4 mt-6 pt-4 border-t border-gray-200">Résultat</h2>
        <RHResultCard result={study.result || null} />
        {!study.result && canCalculate && (
          <div className="text-center mt-4 no-print">
            <button
              onClick={() => setShowCalcModal(true)}
              className="bg-green-600 hover:bg-green-700 text-white px-5 py-2.5 rounded-lg transition-colors"
            >
              Calculer le Rendement Humain
            </button>
          </div>
        )}
      </div>

      {/* ===== TAB : Versions ===== */}
      <div className={activeTab === 'versions' ? '' : 'hidden print:block'}>
        <h2 className="hidden print:block text-xl font-bold text-gray-900 mb-4 mt-6 pt-4 border-t border-gray-200">Versions</h2>
        {(!study.versions || study.versions.length === 0) ? (
          <div className="text-center py-12 text-gray-500">
            Aucune version. Un snapshot est créé automatiquement lors du calcul.
          </div>
        ) : (
          <div className="space-y-3">
            {study.versions.map((ver) => (
              <div key={ver.id} className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs font-bold">v{ver.version}</span>
                    <span className="text-sm text-gray-500">{formatDate(ver.created_at)}</span>
                  </div>
                </div>
                {ver.change_summary && (
                  <p className="text-sm text-gray-600 mt-2">{ver.change_summary}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ===== MODAL CALCUL RH ===== */}
      {showCalcModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-1">Calculer le Rendement Humain</h2>
              <p className="text-sm text-gray-500 mb-6">RH = RTE × PA × D (valeurs entre 0 et 1)</p>

              {calcError && (
                <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg mb-4 text-sm">
                  {calcError}
                </div>
              )}

              <div className="space-y-4">
                {/* RTE */}
                <div className="border border-blue-200 bg-blue-50 rounded-lg p-4">
                  <label className="block text-sm font-bold text-blue-800 mb-2">RTE — Retour Total aux Ménages</label>
                  <div className="grid grid-cols-3 gap-2">
                    <input
                      type="number" step="0.01" min="0" max="1"
                      value={calcForm.rte}
                      onChange={(e) => setCalcForm(prev => ({ ...prev, rte: e.target.value }))}
                      placeholder="0.65"
                      className="border border-blue-300 rounded px-3 py-2 text-sm"
                    />
                    <input
                      type="text"
                      value={calcForm.rte_source}
                      onChange={(e) => setCalcForm(prev => ({ ...prev, rte_source: e.target.value }))}
                      placeholder="Source RTE"
                      className="col-span-2 border border-blue-300 rounded px-3 py-2 text-sm"
                    />
                  </div>
                </div>

                {/* PA */}
                <div className="border border-green-200 bg-green-50 rounded-lg p-4">
                  <label className="block text-sm font-bold text-green-800 mb-2">PA — Pouvoir d'Achat Essentiel</label>
                  <div className="grid grid-cols-3 gap-2">
                    <input
                      type="number" step="0.01" min="0" max="1"
                      value={calcForm.pa}
                      onChange={(e) => setCalcForm(prev => ({ ...prev, pa: e.target.value }))}
                      placeholder="0.67"
                      className="border border-green-300 rounded px-3 py-2 text-sm"
                    />
                    <input
                      type="text"
                      value={calcForm.pa_source}
                      onChange={(e) => setCalcForm(prev => ({ ...prev, pa_source: e.target.value }))}
                      placeholder="Source PA"
                      className="col-span-2 border border-green-300 rounded px-3 py-2 text-sm"
                    />
                  </div>
                </div>

                {/* D */}
                <div className="border border-purple-200 bg-purple-50 rounded-lg p-4">
                  <label className="block text-sm font-bold text-purple-800 mb-2">D — Distribution</label>
                  <div className="grid grid-cols-3 gap-2">
                    <input
                      type="number" step="0.01" min="0" max="1"
                      value={calcForm.d}
                      onChange={(e) => setCalcForm(prev => ({ ...prev, d: e.target.value }))}
                      placeholder="0.75"
                      className="border border-purple-300 rounded px-3 py-2 text-sm"
                    />
                    <input
                      type="text"
                      value={calcForm.d_source}
                      onChange={(e) => setCalcForm(prev => ({ ...prev, d_source: e.target.value }))}
                      placeholder="Source D"
                      className="col-span-2 border border-purple-300 rounded px-3 py-2 text-sm"
                    />
                  </div>
                </div>

                {/* Temps créatif */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Temps créatif annuel (heures) *</label>
                  <input
                    type="number" step="1"
                    value={calcForm.temps_creatif_heures}
                    onChange={(e) => setCalcForm(prev => ({ ...prev, temps_creatif_heures: e.target.value }))}
                    placeholder="2000"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>

                {/* Référence contrôle */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Référence contrôle (comparatif)</label>
                  <textarea
                    value={calcForm.control_reference}
                    onChange={(e) => setCalcForm(prev => ({ ...prev, control_reference: e.target.value }))}
                    placeholder="Cas de référence pour comparaison (ex: pays sans surcoût logement)"
                    rows={2}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>

                {/* Aperçu calcul */}
                {calcForm.rte && calcForm.pa && calcForm.d && (
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <span className="text-sm text-gray-600">Aperçu : </span>
                    <span className="font-mono font-bold text-gray-900">
                      {calcForm.rte} × {calcForm.pa} × {calcForm.d} = {(parseFloat(calcForm.rte) * parseFloat(calcForm.pa) * parseFloat(calcForm.d)).toFixed(4)}
                    </span>
                  </div>
                )}
              </div>

              <div className="flex gap-3 justify-end mt-6 pt-4 border-t border-gray-100">
                <button
                  onClick={() => { setShowCalcModal(false); setCalcError(''); }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  onClick={handleCalculate}
                  disabled={calculating}
                  className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-5 py-2.5 rounded-lg transition-colors disabled:opacity-50"
                >
                  <Calculator size={16} />
                  {calculating ? 'Calcul en cours...' : 'Calculer RH'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== MODAL ÉDITION ===== */}
      {editing && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">Modifier l'étude</h2>
                <button onClick={() => setEditing(false)} className="p-1 hover:bg-gray-100 rounded"><X size={20} className="text-gray-500" /></button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Titre</label>
                  <input type="text" value={editForm.title} onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Niveau</label>
                    <select value={editForm.level} onChange={(e) => setEditForm(prev => ({ ...prev, level: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                      <option value="R1">R1</option><option value="R2">R2</option><option value="R3">R3</option><option value="R4">R4</option><option value="R5">R5</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Domaine</label>
                    <select value={editForm.domain} onChange={(e) => setEditForm(prev => ({ ...prev, domain: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                      <option value="humain">Humain</option><option value="ecologique">Écologique</option><option value="culturel">Culturel</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sous-domaine</label>
                  <input type="text" value={editForm.sub_domain} onChange={(e) => setEditForm(prev => ({ ...prev, sub_domain: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Localisation</label>
                  <input type="text" value={editForm.location} onChange={(e) => setEditForm(prev => ({ ...prev, location: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Début période</label>
                    <input type="date" value={editForm.period_start} onChange={(e) => setEditForm(prev => ({ ...prev, period_start: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Fin période</label>
                    <input type="date" value={editForm.period_end} onChange={(e) => setEditForm(prev => ({ ...prev, period_end: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Population</label>
                  <input type="number" value={editForm.population_size} onChange={(e) => setEditForm(prev => ({ ...prev, population_size: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                </div>
              </div>

              <div className="flex gap-3 justify-end mt-6 pt-4 border-t border-gray-100">
                <button onClick={() => setEditing(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">Annuler</button>
                <button onClick={saveEdit} disabled={editSaving}
                  className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-lg transition-colors disabled:opacity-50">
                  <Check size={16} />
                  {editSaving ? 'Enregistrement...' : 'Enregistrer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== MODAL SUPPRESSION ===== */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-6">
            <div className="text-center mb-6">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Trash2 size={24} className="text-red-600" />
              </div>
              <h2 className="text-lg font-bold text-gray-900">Supprimer cette étude ?</h2>
              <p className="text-sm text-gray-500 mt-2">"{study.title}" sera définitivement supprimée avec toutes ses données (chaîne, Bradford, résultat, versions).</p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
                Annuler
              </button>
              <button onClick={handleDelete} disabled={deleting}
                className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50">
                {deleting ? 'Suppression...' : 'Supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RHStudyDetail;
